import React, { useEffect } from 'react';
import { Award, Users, Heart, Wrench } from 'lucide-react';
import { ourStory } from '../data/mockData';

const Story = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-burgundy text-cream py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6">
            {ourStory.title}
          </h1>
          <p className="text-2xl font-serif max-w-3xl mx-auto leading-relaxed text-cream/90">
            {ourStory.subtitle}
          </p>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              {/* Historic Photo */}
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src={ourStory.images[1]}
                  alt="Salumificio Lombardi negli anni passati"
                  className="w-full h-[500px] object-cover" />

              </div>

              {/* Story Text */}
              <div>
                <div className="space-y-6 text-wood-brown font-serif text-lg leading-relaxed">
                  {ourStory.story.split('\n\n').map((paragraph, index) =>
                  <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-beige">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-burgundy text-center mb-16">
            I Nostri Valori
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {ourStory.values.map((value, index) => {
              const icons = [Heart, Award, Wrench, Users];
              const Icon = icons[index];

              return (
                <div key={index} className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 text-center">
                  <div className="w-20 h-20 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={40} className="text-burgundy" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-burgundy mb-4">
                    {value.title}
                  </h3>
                  <p className="text-wood-brown font-serif leading-relaxed">
                    {value.description}
                  </p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Then and Now Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-burgundy text-center mb-16">
            Ieri e Oggi
          </h2>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Then */}
              <div className="bg-white rounded-lg overflow-hidden shadow-xl">
                <img
                  src={ourStory.images[0]}
                  alt="Il negozio come era"
                  className="w-full h-[400px] object-cover" />

                <div className="p-6">
                  <h3 className="font-serif text-2xl font-bold text-burgundy mb-3">
                    Gli Inizi - Anni '50
                  </h3>
                  <p className="text-wood-brown font-serif leading-relaxed">
                    Tutto ebbe inizio con la felice intuizione del nonno Giovanni, che decise 
                    di dedicarsi alla lavorazione delle carni, dando vita a quella che sarebbe 
                    diventata una tradizione familiare.
                  </p>
                </div>
              </div>

              {/* Now */}
              <div className="bg-white rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_lombardi-salumi/artifacts/v44yozih_IMG_0041%20copia.webp"
                  alt="Il salumificio oggi"
                  className="w-full h-[400px] object-cover" />

                <div className="p-6">
                  <h3 className="font-serif text-2xl font-bold text-burgundy mb-3">
                    Oggi - Tre Generazioni
                  </h3>
                  <p className="text-wood-brown font-serif leading-relaxed">Oggi, guidato da una nuova generazione di Lombardi, il salumificio continua la tradizione con la stessa passione e dedizione, servendo tutta l'Emilia Romagna e oltre con prodotti di eccellenza.



                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-wood-brown text-cream">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-center mb-16">
            La Nostra Storia in Date
          </h2>

          <div className="max-w-4xl mx-auto space-y-12">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-cream text-burgundy rounded-full flex items-center justify-center font-serif font-bold text-xl">
                  1957
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-2xl font-bold mb-3">La Nascita</h3>
                <p className="font-serif leading-relaxed text-cream/90">
                  Il nonno Giovanni fonda il Salumificio Lombardi a Santarcangelo di Romagna, 
                  iniziando la tradizione della lavorazione artigianale delle carni.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-cream text-burgundy rounded-full flex items-center justify-center font-serif font-bold text-xl">
                  1970s
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-2xl font-bold mb-3">L'Espansione</h3>
                <p className="font-serif leading-relaxed text-cream/90">
                  Sotto la guida del signor Tonino Lombardi, l'azienda conosce un florido sviluppo, 
                  diventando un punto di riferimento in tutta l'Emilia Romagna.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-cream text-burgundy rounded-full flex items-center justify-center font-serif font-bold text-xl">
                  Oggi
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-2xl font-bold mb-3">La Continuità</h3>
                <p className="font-serif leading-relaxed text-cream/90">
                  La terza generazione della famiglia Lombardi porta avanti con orgoglio la tradizione, 
                  mantenendo gli stessi standard di qualità e autenticità dei primi giorni.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default Story;